import { gql } from '@apollo/client/core';

export const findStickerCollectionsForSticker = gql`
  query FindStickerCollectionsForSticker($stickerId: UUID!, $orgId: UUID) {
    findStickerCollectionsForSticker(stickerId: $stickerId, orgId: $orgId) {
      id
      title
      slug
      ownerName
      ownerAvatar
      creatorNote
      showProgress
      totalStickers
      stickersRequiredToComplete
      collectedCount
      isStarted
      isCompleted
      completedAt
      items {
        id
        sortOrder
        clue
        requiresCode
        collected
        collectedAt
        sticker {
          id
          title
          status
          slug
          url
          thumbnailUrl
          creatorName
          creatorAvatar
        }
      }
      updates {
        id
        body
        postedAt
      }
    }
  }
`;

export const findStickerCollections = gql`
  query FindStickerCollections(
    $orgId: UUID
    $status: String
    $skip: Int!
    $take: Int!
  ) {
    findStickerCollections(
      orgId: $orgId
      status: $status
      skip: $skip
      take: $take
    ) {
      id
      title
      slug
      status
      totalStickers
      publishedAt
    }
  }
`;

export const findStickerCollection = gql`
  query FindStickerCollection($id: UUID, $slug: String, $orgId: UUID) {
    findStickerCollection(id: $id, slug: $slug, orgId: $orgId) {
      id
      title
      slug
      entityId
      description
      coverImageUrl
      ownerName
      ownerAvatar
      creatorNote
      showProgress
      allowCodeReuse
      requireAllStickersToComplete
      stickersRequiredToComplete
      sendCompletionNotification
      codeFormat
      defaultCodeMaxUses
      status
      totalStickers
      collectedCount
      isCompleted
      completedAt
      items {
        id
        sortOrder
        clue
        requiresCode
        collected
        sticker {
          id
          title
          status
          slug
          url
          thumbnailUrl
          cost
          campaignType
          entityId
          creatorName
          creatorAvatar
        }
        codes {
          id
          code
          maxUses
          uses
          isActive
        }
      }
      updates {
        id
        body
        postedAt
      }
    }
  }
`;

export const findMyStickerCollections = gql`
  query FindMyStickerCollections($orgId: UUID) {
    findMyStickerCollections(orgId: $orgId) {
      id
      title
      slug
      coverImageUrl
      ownerName
      ownerAvatar
      creatorNote
      showProgress
      status
      totalStickers
      stickersRequiredToComplete
      collectedCount
      isCompleted
      completedAt
      items {
        id
        sortOrder
        clue
        collected
        sticker {
          id
          title
          status
          slug
          url
          thumbnailUrl
        }
      }
      updates {
        id
        body
        postedAt
      }
    }
  }
`;

export const findStickerCollectionAnalytics = gql`
  query FindStickerCollectionAnalytics(
    $collectionId: UUID!
    $filter: String
    $search: String
    $skip: Int!
    $take: Int!
  ) {
    findStickerCollectionAnalytics(
      collectionId: $collectionId
      filter: $filter
      search: $search
      skip: $skip
      take: $take
    ) {
      totalStickers
      stickersRequiredToComplete
      startedCount
      completedCount
      inProgressCount
      averageProgressPercent
      totalCollectors
      collectors {
        isIdentityDisclosed
        name
        email
        avatar
        startedAt
        collectedCount
        totalStickers
        isCompleted
        completedAt
      }
      items {
        itemId
        title
        url
        collectorCount
        codeRedemptions
      }
    }
  }
`;

export const mutateStickerCollection = gql`
  mutation MutateStickerCollection($input: StickerCollectionInput!) {
    mutateStickerCollection(input: $input) {
      id
      slug
      status
      requireAllStickersToComplete
      stickersRequiredToComplete
      sendCompletionNotification
      codeFormat
      defaultCodeMaxUses
    }
  }
`;

export const mutateStickerCollectionStatus = gql`
  mutation MutateStickerCollectionStatus($collectionId: UUID!, $status: String!) {
    mutateStickerCollectionStatus(collectionId: $collectionId, status: $status) {
      id
      status
      publishedAt
    }
  }
`;

export const removeStickerCollection = gql`
  mutation RemoveStickerCollection($collectionId: UUID!) {
    removeStickerCollection(collectionId: $collectionId)
  }
`;

export const mutateStickerCollectionItem = gql`
  mutation MutateStickerCollectionItem($input: StickerCollectionItemInput!) {
    mutateStickerCollectionItem(input: $input) {
      id
      sortOrder
      requiresCode
      clue
      codes {
        id
        code
        maxUses
        uses
        isActive
      }
    }
  }
`;

export const removeStickerCollectionItem = gql`
  mutation RemoveStickerCollectionItem($itemId: UUID!) {
    removeStickerCollectionItem(itemId: $itemId)
  }
`;

export const mutateStickerCollectionCode = gql`
  mutation MutateStickerCollectionCode($input: StickerCollectionCodeInput!) {
    mutateStickerCollectionCode(input: $input) {
      id
      code
      maxUses
      uses
      isActive
    }
  }
`;

export const removeStickerCollectionCode = gql`
  mutation RemoveStickerCollectionCode($codeId: UUID!) {
    removeStickerCollectionCode(codeId: $codeId)
  }
`;

export const mutateStickerCollectionUpdate = gql`
  mutation MutateStickerCollectionUpdate($input: StickerCollectionUpdateInput!) {
    mutateStickerCollectionUpdate(input: $input) {
      id
      body
      postedAt
    }
  }
`;

export const removeStickerCollectionUpdate = gql`
  mutation RemoveStickerCollectionUpdate($updateId: UUID!) {
    removeStickerCollectionUpdate(updateId: $updateId)
  }
`;

export const redeemStickerCollectionCode = gql`
  mutation RedeemStickerCollectionCode(
    $stickerId: UUID!
    $code: String!
    $orgId: UUID
  ) {
    redeemStickerCollectionCode(
      stickerId: $stickerId
      code: $code
      orgId: $orgId
    ) {
      collectionId
      collectionTitle
      collectedCount
      totalStickers
      stickersRequiredToComplete
      isCompleted
      justCompleted
      wasAlreadyCollected
    }
  }
`;
